import {Service, service, inject} from "./di";
import {PostcardService} from "./PostcardService";
import {ConfigService} from "./ConfigService";
import {LocationService} from "./LocationService";
import {AuthProviderUser} from "../services/definitions/IAuthenticationService";
import {AuthProvider, AccountsUser, LoyaltyCard, LoyaltyCardJSON, RatingObject} from "../definitions/CommonDefinitions";
import {fetch, fetchJSON, FetchError, FetchOptions} from "../utils/fetch";
import * as i18n from "../i18n/I18n";
import * as array from "../utils/array";
import { User } from "./AuthenticationService";

const CLIENT_ID = "client-flipp-web";

interface LoyaltyCardOperationJSON {
	id: string;
	client_id: string;
	commit_version: string;
	name: string;
	loyalty_program_id: number;
	card_id: string;
	phone_number: string;
	created_at: string;
	updated_at: string;
}

interface PostLoyaltyCardJSON {
	created: LoyaltyCardOperationJSON[];
	failed: LoyaltyCardOperationJSON[];
}

interface DeleteLoyaltyCardJSON {
	deleted: LoyaltyCardOperationJSON[];
	failed: LoyaltyCardOperationJSON[];
}

@service
export class AccountsService extends Service {
	@inject postcardService: PostcardService;
	@inject locationService: LocationService;
	@inject config: ConfigService;

	fetchToken(user: AuthProviderUser) {
		const body: {[key: string]: string} = {
			"platform": "web"
		};
		switch (user.provider) {
			case AuthProvider.GOOGLE:
				body["google_id_token"] = user.accessToken; break;
			case AuthProvider.FACEBOOK:
				body["facebook_access_token"] = user.accessToken; break;
		}
		const opts: FetchOptions = {
			url: `${this.config.ACCOUNTS_SERVER}/token`,
			method: "POST",
			headers: [
				{"Content-Type": "application/json"}
			],
			body: JSON.stringify(body)
		};
		return fetchJSON<AccountsUser>(opts);
	}

	revokeToken(token: AccountsUser) {
		const body: {[key: string]: string} = {
			"platform": "web"
		};
		const opts: FetchOptions = {
			url: `${this.config.ACCOUNTS_SERVER}/users/${token.user_id}/revoke_token`,
			method: "POST",
			headers: [
				{"Content-Type": "application/json"},
				{"Authorization": `Token token=${token.access_token}`}
			],
			body: JSON.stringify(body)
		};
		return fetch<void>(opts, [200]);
	}

	getUser(token: AccountsUser) {
		const opts: FetchOptions = {
			url: `${this.config.ACCOUNTS_SERVER}/v1/users/${token.user_id}`,
			method: "GET",
			headers: [
				{"Content-Type": "application/json"},
				{"Authorization": `Token token=${token.access_token}`}
			]
		};
		return fetch<AccountsUser>(opts, [200]);
	}

	updateSubscription(token: AccountsUser, subscribed: boolean) {
		const body: {[key: string]: string} = {
			"platform": "web"
		};
		const opts: FetchOptions = {
			url: `${this.config.ACCOUNTS_SERVER}/v1/users/${token.user_id}?email_subscribed=${subscribed}`,
			method: "PUT",
			headers: [
				{"Content-Type": "application/json"},
				{"Authorization": `Token token=${token.access_token}`}
			],
			body: JSON.stringify(body)
		};
		return fetch<void>(opts, [200]);
	}

	getLoyaltyCards(token: AccountsUser) {
		const opts: FetchOptions = {
			url: `${this.config.ACCOUNTS_SERVER}/v1/users/${token.user_id}/loyalty_cards`,
			method: "GET",
			headers: [
				{"Accept": "application/json"},
				{"Authorization": `Token token=${token.access_token}`}
			]
		};

		return fetch<LoyaltyCardJSON>(opts, [200]);
	}

	postLoyaltyCards(token: AccountsUser, card: LoyaltyCard) {
		return this.postcardService.registerLoyaltyCard(token.user_id, card).then(() => {
			const serializedCards = [card].map((card) => ({
				client_id: CLIENT_ID,
				card_id: card.cardId,
				loyalty_program_id: card.loyaltyProgramId,
				name: card.name,
				phone_number: card.phoneNumber
			}));
			const opts: FetchOptions = {
				url: `${this.config.ACCOUNTS_SERVER}/v1/users/${token.user_id}/loyalty_cards/batch_create`,
				method: "POST",
				headers: [
					{"Accept": "application/json"},
					{"Content-Type": "application/json"},
					{"Authorization": `Token token=${token.access_token}`}
				],
				body: JSON.stringify({
					loyalty_cards: serializedCards
				})
			};

			return fetch<PostLoyaltyCardJSON>(opts, [200]).then((resp: PostLoyaltyCardJSON) => {
				const success = array.find(resp.created, (createdCard: LoyaltyCardOperationJSON) => {
					if (card.phoneNumber) {
						return card.phoneNumber === createdCard.phone_number;
					} else if (card.cardId) {
						return card.cardId === createdCard.card_id;
					} else {
						return false;
					}
				});

				if (!success) {
					throw new Error(i18n.i18n("add_card_error"));
				}
			});
		}).catch((err: Error) => {
			if (err instanceof FetchError) {
				const body = JSON.parse(err.body);
				throw new Error(this.postcardService.getErrorMessage(body.error_code));
			} else {
				throw new Error(i18n.i18n("add_card_error"));
			}
		});
	}

	deleteLoyaltyCards(token: AccountsUser, card: LoyaltyCard) {
		return this.postcardService.unregisterLoyaltyCard(token.user_id, card).then(() => {
			const serializedCards = [card].map((card) => ({
				client_id: CLIENT_ID,
				id: card.id,
				last_commit_version: card.commitVersion
			}));
			const opts: FetchOptions = {
				url: `${this.config.ACCOUNTS_SERVER}/v1/users/${token.user_id}/loyalty_cards/batch_delete`,
				method: "POST",
				headers: [
					{"Accept": "application/json"},
					{"Content-Type": "application/json"},
					{"Authorization": `Token token=${token.access_token}`}
				],
				body: JSON.stringify({
					loyalty_cards: serializedCards
				})
			};

			return fetch<DeleteLoyaltyCardJSON>(opts, [200]).then((resp: DeleteLoyaltyCardJSON) => {
				const success = array.find(resp.deleted,
					(entry: LoyaltyCardOperationJSON) => entry.id === card.id);

				if (!success) {
					throw new Error(i18n.i18n("remove_card_error"));
				}
			});
		});
	}

	deleteUserData(data: any) {
		const body: {[key: string]: string} = {
			"email": data.email,
			"sid" : data.sid,
			"fsa" : data.fsa,
			"locale" : data.locale,
			"platform": "flipp_web",
			"application_version" : data.application_version,
			"country_code" : data.country_code
		};
		const opts: FetchOptions = {
			url: `${this.config.ACCOUNTS_SERVER}/v1/users/${data.user_id}/privacy/delete`,
			method: "POST",
			headers: [
				{"Content-Type": "application/json"},
				{"Authorization": `Token token=${data.access_token}`}
			],
			body: JSON.stringify(body)
		};
		return fetch<void>(opts, [200]);
	}

	receiveCopyData(data: any) {
		const body: {[key: string]: string} = {
			"first_name": data.firstName,
			"last_name": data.lastName,
			"email": data.email,
			"resident_of": data.residentOf,
			"sid" : data.sid,
			"fsa" : data.fsa,
			"locale" : data.locale,
			"platform": "flipp_web",
			"application_version" : data.application_version,
			"country_code" : data.country_code
		};
		const opts: FetchOptions = {
			url: `${this.config.ACCOUNTS_SERVER}/v1/users/${data.user_id}/privacy/request_data`,
			method: "POST",
			headers: [
				{"Content-Type": "application/json"},
				{"Authorization": `Token token=${data.access_token}`}
			],
			body: JSON.stringify(body)
		};
		return fetch<void>(opts, [200]);
	}

	notSellData(data: any) {
		const body: {[key: string]: string} = {
			"first_name": data.firstName,
			"last_name": data.lastName,
			"email": data.email,
			"resident_of": data.residentOf,
			"sid" : data.sid,
			"fsa" : data.fsa,
			"locale" : data.locale,
			"platform": "flipp_web",
			"application_version" : data.application_version,
			"country_code" : data.country_code
		};
		const opts: FetchOptions = {
			url: `${this.config.ACCOUNTS_SERVER}/v1/users/${data.user_id}/privacy/do_not_sell`,
			method: "POST",
			headers: [
				{"Content-Type": "application/json"},
				{"Authorization": `Token token=${data.access_token}`}
			],
			body: JSON.stringify(body)
		};
		return fetch<void>(opts, [200]);
	}

	getAppRating () {
		const { locale } = this.locationService;
		const opts: FetchOptions = {
			url: `${this.config.FLIPP_COM_APIS}/app/ratings/${locale}`,
		};

		return fetchJSON<RatingObject>(opts);
	}
}
